import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../../firebase/firebase';
import {
  collection, addDoc, getDocs, query, where, deleteDoc, doc, setDoc,
} from 'firebase/firestore';
import WIcon from '../../components/shared/WIcon';
import Modal from '../../components/shared/Modal';

// ─── Mock data definitions ────────────────────────────────────────────────

const MOCK_FLAG = { mockData: true };

const MOCK_COMPANIES = [
  { name: 'Bank of Georgia',  description: 'Largest commercial bank in Georgia.',          email: 'admin+bog@loysta.demo' },
  { name: 'TBC Bank',         description: 'Modern banking, built around the customer.',   email: 'admin+tbc@loysta.demo' },
  { name: 'Magti',            description: 'Telecommunications and connectivity leader.',  email: 'admin+magti@loysta.demo' },
  { name: 'Silknet',          description: 'Internet, mobile and home services.',          email: 'admin+silknet@loysta.demo' },
];

// Curated stable Unsplash photo URLs — Unsplash license permits free
// commercial use without attribution. Listed by category.
const IMAGES_BY_CATEGORY = {
  food: [
    'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=900&q=80',  // pizza
    'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=900&q=80',  // pancakes
    'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&q=80',     // bowl
    'https://images.unsplash.com/photo-1568376794508-ae52c6ab3929?w=900&q=80',  // burger
    'https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=900&q=80',     // pasta
  ],
  health: [
    'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=900&q=80',  // yoga
    'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=900&q=80',  // gym
    'https://images.unsplash.com/photo-1599058917212-d750089bc07e?w=900&q=80',  // spa
    'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=900&q=80',  // wellness
  ],
  travel: [
    'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=900&q=80',  // suitcase
    'https://images.unsplash.com/photo-1503220317375-aaad61436b1b?w=900&q=80',  // mountains
    'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=900&q=80',  // beach
    'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=900&q=80',  // beach umbrella
  ],
  shopping: [
    'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=900&q=80',  // shopping bags
    'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=900&q=80',  // boutique
    'https://images.unsplash.com/photo-1585488763217-17b75e1ae74f?w=900&q=80',  // store
  ],
  entertainment: [
    'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=900&q=80',  // cinema
    'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=900&q=80',  // concert
    'https://images.unsplash.com/photo-1542204165-65bf26472b9b?w=900&q=80',     // arcade
  ],
  tech: [
    'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=900&q=80',  // laptop
    'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=900&q=80',  // gadget
    'https://images.unsplash.com/photo-1544866092-1935c5ef2a8f?w=900&q=80',     // headphones
  ],
  finance: [
    'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=900&q=80',     // papers
    'https://images.unsplash.com/photo-1556742044-3c52d6e88c62?w=900&q=80',     // notebook
    'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=900&q=80',  // calculator
  ],
  other: [
    'https://images.unsplash.com/photo-1520975916090-3105956dac38?w=900&q=80',  // gift
    'https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=900&q=80',  // generic
  ],
};

const MOCK_VENDORS = [
  { businessName: "Eli's Pizza",         category: 'food',           description: 'Wood-fired Neapolitan pizza, made fresh from scratch every morning.', location: 'Vake, Tbilisi' },
  { businessName: 'Coffee Lab',          category: 'food',           description: 'Specialty coffee, single-origin pour-overs, and house-baked pastries.', location: 'Saburtalo, Tbilisi' },
  { businessName: 'Yoga Studio Tbilisi', category: 'health',         description: 'Drop-in vinyasa, hatha and yin classes seven days a week.', location: 'Vera, Tbilisi' },
  { businessName: 'AlpineMed Clinic',    category: 'health',         description: 'Family medicine and preventive checkups, English-speaking doctors.', location: 'Saburtalo, Tbilisi' },
  { businessName: 'GoTrip Adventures',   category: 'travel',         description: 'Weekend trips to Kazbegi, Svaneti and Mtskheta from Tbilisi.', location: 'Old Tbilisi' },
  { businessName: 'City Cinemas',        category: 'entertainment',  description: 'Latest blockbusters in IMAX and premium recliner seats.', location: 'East Point, Tbilisi' },
  { businessName: 'TechHub Store',       category: 'tech',           description: 'Apple authorized reseller, premium accessories and trade-in program.', location: 'Galleria Tbilisi' },
  { businessName: 'Velvet Boutique',     category: 'shopping',       description: 'Independent designer fashion, handpicked European labels.', location: 'Rustaveli Avenue' },
];

const OFFER_TEMPLATES = {
  food: [
    { title: 'Buy 1 Get 1 Free Pizza', discount: 50, description: 'Order any large pizza and get a second one free. Valid for dine-in only.' },
    { title: '20% Off Lunch Menu',     discount: 20, description: 'Enjoy 20% off any lunch combo, weekdays from 12:00–15:00.' },
    { title: 'Free Coffee with Brunch',discount: 15, description: 'Complimentary house coffee with any brunch order on weekends.' },
  ],
  health: [
    { title: 'First Class Free',       discount: 100, description: 'Try your first yoga or wellness class on the house.' },
    { title: '30% Off Monthly Pass',   discount: 30,  description: 'Get 30% off your first month of unlimited classes.' },
    { title: 'Annual Checkup Discount',discount: 25,  description: '25% off the standard annual checkup package.' },
  ],
  travel: [
    { title: '15% Off Weekend Tours',  discount: 15, description: 'Save on guided trips to Kazbegi, Svaneti and Mtskheta. Booking required 48h ahead.' },
    { title: 'Group Discount: 4+ pax', discount: 25, description: 'Bring 4 or more people and save 25% on any tour.' },
    { title: 'Off-Season Deal',        discount: 30, description: '30% off all tours from November through March.' },
  ],
  shopping: [
    { title: 'New Season Discount',    discount: 20, description: '20% off the entire new season collection. In-store only.' },
    { title: 'Birthday Month Special', discount: 10, description: '10% off everything during your birthday month, year-round.' },
    { title: 'Clearance: 40% Off',     discount: 40, description: 'End-of-season clearance — 40% off marked items.' },
  ],
  entertainment: [
    { title: 'Tuesday Movie Night',    discount: 50, description: '50% off any movie ticket every Tuesday.' },
    { title: 'Combo: Ticket + Snack',  discount: 30, description: 'Save 30% when you bundle a ticket with popcorn and drinks.' },
    { title: 'Family Pack',            discount: 25, description: '25% off when you buy 4 or more tickets in one transaction.' },
  ],
  tech: [
    { title: '10% Off Accessories',    discount: 10, description: '10% off all cases, cables, chargers and screen protectors.' },
    { title: 'Trade-In Bonus: +15%',   discount: 15, description: 'Extra 15% credit on top of standard trade-in valuation.' },
    { title: 'Free 30-Day Support',    discount: 0,  description: 'Free in-store technical support for 30 days after purchase.' },
  ],
  finance: [
    { title: 'No-Fee Account Setup',   discount: 100, description: 'Skip the account opening fee for new customers.' },
    { title: 'Free Notary for 1 Year', discount: 100, description: 'Free in-branch notary services for one full year.' },
  ],
  other: [
    { title: 'Welcome Gift',           discount: 10, description: '10% off your first purchase.' },
  ],
};

const FIRST_NAMES = ['Mariam', 'Tamar', 'Nino', 'Salome', 'Ana', 'Nika', 'Giorgi', 'Levan', 'Davit', 'Luka', 'Ana', 'Tinatin', 'Beka', 'Sandro', 'Mariam'];
const LAST_NAMES  = ['Beridze', 'Tchanturia', 'Kapanadze', 'Akhvlediani', 'Bakuradze', 'Lortkipanidze', 'Chkheidze', 'Tsereteli', 'Sulaberidze', 'Gogoladze'];

const MOCK_COLLECTIONS = ['users', 'companies', 'vendors', 'offers', 'vendorStaff', 'vendorAdmins'];

// ─── Helpers ──────────────────────────────────────────────────────────────

const pick = (arr, i) => arr[i % arr.length];
const randomDate = (daysFromNow) => {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d;
};

const generateAll = async ({ onProgress }) => {
  const counts = { companies: 0, vendors: 0, vendorAdmins: 0, offers: 0, users: 0, vendorStaff: 0 };

  // 1. Companies
  onProgress?.('Generating companies…');
  const companyIds = [];
  for (const c of MOCK_COMPANIES) {
    const ref = await addDoc(collection(db, 'companies'), {
      ...c,
      logoUrl: null,
      employeeCount: 5,
      createdAt: new Date(),
      ...MOCK_FLAG,
    });
    companyIds.push(ref.id);
    counts.companies++;
  }

  // 2. Vendors — also seed vendorAdmins/{email} so they show up in the
  //    super admin's Vendors list (the page builds its rows from that
  //    whitelist joined back to the vendors collection).
  onProgress?.('Generating vendors…');
  const vendors = []; // { id, businessName, category }
  for (const v of MOCK_VENDORS) {
    const heroImage = pick(IMAGES_BY_CATEGORY[v.category] || IMAGES_BY_CATEGORY.other, 0);
    const slug = v.businessName.toLowerCase().replace(/[^a-z]/g, '');
    const adminEmail = `admin@${slug}.demo`;
    const vendorRef = await addDoc(collection(db, 'vendors'), {
      name: v.businessName,
      businessName: v.businessName,
      description: v.description,
      location: v.location,
      contactInfo: '+995 32 200 0000',
      website: '',
      serviceHours: 'Mon–Sun 10:00–22:00',
      email: adminEmail,
      logoUrl: heroImage,
      images: [{
        id: 'main',
        url: heroImage,
        name: `${v.businessName} hero`,
        path: `mock/${v.businessName}.jpg`,
      }],
      mainPhotoId: 'main',
      isActive: true,
      authUid: null,
      createdAt: new Date(),
      ...MOCK_FLAG,
    });
    await setDoc(doc(db, 'vendorAdmins', adminEmail), {
      email: adminEmail,
      vendorId: vendorRef.id,
      businessName: v.businessName,
      status: 'active',
      addedAt: new Date(),
      addedBy: auth.currentUser?.email || 'super-admin@loysta.demo',
      ...MOCK_FLAG,
    });
    vendors.push({ id: vendorRef.id, businessName: v.businessName, category: v.category });
    counts.vendors++;
    counts.vendorAdmins++;
  }

  // 3. Offers — per vendor, pull templates by category, pick a category image
  onProgress?.('Generating offers…');
  for (const v of vendors) {
    const templates = OFFER_TEMPLATES[v.category] || OFFER_TEMPLATES.other;
    const images = IMAGES_BY_CATEGORY[v.category] || IMAGES_BY_CATEGORY.other;
    for (let i = 0; i < templates.length; i++) {
      const t = templates[i];
      const img = pick(images, i + 1);
      const minTier = String(1 + (i % 3)); // 1 / 2 / 3 rotates
      const tiers = ['1', '2', '3'].filter(x => parseInt(x, 10) >= parseInt(minTier, 10));
      await addDoc(collection(db, 'offers'), {
        title: t.title,
        description: t.description,
        discountPercentage: String(t.discount),
        category: v.category,
        imageUrl: img,
        companyIds: companyIds, // visible to all mock companies
        tiers,
        minTierLevel: minTier,
        vendorId: v.id,
        vendorName: v.businessName,
        startDate: randomDate(-7).toISOString().slice(0, 10),
        endDate: randomDate(60).toISOString().slice(0, 10),
        createdAt: new Date(),
        ...MOCK_FLAG,
      });
      counts.offers++;
    }
  }

  // 4. Employees — 5 per company, sequential phones
  onProgress?.('Generating employees…');
  let phoneSeq = 1;
  for (const companyId of companyIds) {
    for (let i = 0; i < 5; i++) {
      const first = pick(FIRST_NAMES, phoneSeq + 3);
      const last  = pick(LAST_NAMES,  phoneSeq + 7);
      const phone = `+99555500${String(phoneSeq).padStart(4, '0')}`;
      // Tier distribution: 3 × T1, 1 × T2, 1 × T3
      const tier = i < 3 ? 1 : (i === 3 ? 2 : 3);
      await addDoc(collection(db, 'users'), {
        name: first,
        surname: last,
        phone,
        email: null,
        tier,
        companyId,
        addedBy: auth.currentUser?.email || 'super-admin@loysta.demo',
        isActive: true,
        createdAt: new Date(),
        ...MOCK_FLAG,
      });
      counts.users++;
      phoneSeq++;
    }
  }

  // 5. One super user — bypasses both company and tier filters in the
  //    Flutter employee app. Used for end-to-end demo / QA.
  onProgress?.('Generating super user…');
  await addDoc(collection(db, 'users'), {
    name: 'Super',
    surname: 'Admin',
    phone: '+995555099999',
    email: null,
    tier: 'super',
    companyId: '',
    addedBy: auth.currentUser?.email || 'super-admin@loysta.demo',
    isActive: true,
    createdAt: new Date(),
    ...MOCK_FLAG,
  });
  counts.users++;

  // 6. Vendor staff — 1 per vendor, sequential phones in 1XXXX range
  onProgress?.('Generating vendor staff…');
  let staffSeq = 1;
  for (const v of vendors) {
    const first = pick(FIRST_NAMES, staffSeq + 11);
    const last  = pick(LAST_NAMES,  staffSeq + 5);
    const phone = `+99555510${String(staffSeq).padStart(4, '0')}`;
    await addDoc(collection(db, 'vendorStaff'), {
      name: `${first} ${last}`,
      phone,
      vendorId: v.id,
      isActive: true,
      addedBy: auth.currentUser?.email || 'super-admin@loysta.demo',
      createdAt: new Date(),
      ...MOCK_FLAG,
    });
    counts.vendorStaff++;
    staffSeq++;
  }

  return counts;
};

const deleteAllMock = async ({ onProgress }) => {
  const counts = {
    companies: 0, vendors: 0, vendorAdmins: 0,
    offers: 0, users: 0, vendorStaff: 0, redemptions: 0,
  };
  // We also clear redemptions tied to mock users (they wouldn't have the flag,
  // so query separately).
  const userQuerySnap = await getDocs(query(
    collection(db, 'users'),
    where('mockData', '==', true),
  ));
  const mockUserIds = new Set(userQuerySnap.docs.map(d => d.id));

  for (const col of MOCK_COLLECTIONS) {
    onProgress?.(`Deleting ${col}…`);
    const snap = await getDocs(query(collection(db, col), where('mockData', '==', true)));
    for (const d of snap.docs) {
      await deleteDoc(d.ref);
      counts[col]++;
    }
  }

  // Redemptions tied to mock users
  if (mockUserIds.size > 0) {
    onProgress?.('Deleting mock redemptions…');
    const allRed = await getDocs(collection(db, 'redemptions'));
    for (const d of allRed.docs) {
      const uid = d.data().userId;
      if (uid && mockUserIds.has(uid)) {
        await deleteDoc(d.ref);
        counts.redemptions++;
      }
    }
  }

  return counts;
};

const countAllMock = async () => {
  const counts = {
    companies: 0, vendors: 0, vendorAdmins: 0,
    offers: 0, users: 0, vendorStaff: 0,
  };
  for (const col of MOCK_COLLECTIONS) {
    const snap = await getDocs(query(collection(db, col), where('mockData', '==', true)));
    counts[col] = snap.size;
  }
  return counts;
};

// ─── UI ──────────────────────────────────────────────────────────────────

const Stat = ({ label, value }) => (
  <div style={{
    flex: 1, minWidth: 120,
    background: '#f8fafc', border: '1.5px solid #e2e8f0',
    borderRadius: 10, padding: '14px 16px',
  }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.6, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>{value}</div>
  </div>
);

const MockData = () => {
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState(null); // {type, message}
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const c = await countAllMock();
      setCounts(c);
    } catch (err) {
      console.error('refresh counts failed', err);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleGenerate = async () => {
    setBusy(true);
    setStatus(null);
    setProgress('Starting…');
    try {
      const result = await generateAll({ onProgress: setProgress });
      setStatus({
        type: 'success',
        message: `Generated ${result.companies} companies, ${result.vendors} vendors (${result.vendorAdmins} admins), ${result.offers} offers, ${result.users} users (incl. 1 super user) and ${result.vendorStaff} vendor staff.`,
      });
      await refresh();
    } catch (err) {
      console.error('generate failed', err);
      setStatus({ type: 'error', message: err.message || 'Failed to generate.' });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    setBusy(true);
    setStatus(null);
    setProgress('Starting…');
    try {
      const result = await deleteAllMock({ onProgress: setProgress });
      setStatus({
        type: 'success',
        message: `Deleted ${result.companies} companies, ${result.vendors} vendors, ${result.vendorAdmins} vendor admins, ${result.offers} offers, ${result.users} users, ${result.vendorStaff} vendor staff and ${result.redemptions} mock redemptions.`,
      });
      await refresh();
    } catch (err) {
      console.error('delete failed', err);
      setStatus({ type: 'error', message: err.message || 'Failed to delete.' });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const totalMock = counts
    ? counts.companies + counts.vendors + counts.vendorAdmins
      + counts.offers + counts.users + counts.vendorStaff
    : 0;

  return (
    <>
      {confirmDelete && (
        <Modal title="Delete all mock data?" onClose={() => setConfirmDelete(false)} width={460}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: 0 }}>
              This removes every Firestore doc with <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>mockData: true</code> across companies, vendors, offers, users and vendor staff. Real data is untouched. Mock-user redemptions are also cleaned up.
            </p>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
              Currently in Firestore: <strong>{totalMock}</strong> mock docs.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="cancel-btn" style={{ flex: 1 }} onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button type="button" className="btn btn-danger" style={{ flex: 1, justifyContent: 'center' }} onClick={handleDelete}>Delete all</button>
            </div>
          </div>
        </Modal>
      )}

      <div className="loysta-card" style={{ padding: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: 0 }}>Mock Data Generator</h2>
          <p style={{ fontSize: 13, color: '#64748b', margin: '6px 0 0' }}>
            Seed Firestore with realistic-looking presentation data — companies, vendors with images, offers, employees and vendor staff. Everything is tagged so it can be removed cleanly.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 18 }}>
          <Stat label="Companies"   value={counts?.companies    ?? '—'} />
          <Stat label="Vendors"     value={counts?.vendors      ?? '—'} />
          <Stat label="Offers"      value={counts?.offers       ?? '—'} />
          <Stat label="Users"       value={counts?.users        ?? '—'} />
          <Stat label="Vendor staff" value={counts?.vendorStaff ?? '—'} />
        </div>

        {status && (
          <div className={`loysta-alert ${status.type}`} style={{ marginTop: 16 }}>
            {status.message}
          </div>
        )}
        {busy && progress && (
          <div style={{
            marginTop: 16, padding: '12px 14px',
            background: '#f8fafc', border: '1.5px solid #e2e8f0',
            borderRadius: 8, fontSize: 13, color: '#475569',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div className="loading-spinner-sm" />
            <span>{progress}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 22 }}>
          <button
            type="button"
            disabled={busy}
            onClick={handleGenerate}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px',
              background: busy ? '#94a3b8' : '#00bf63',
              color: 'white', border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            <WIcon name="plus" size={15} color="white" />
            Generate mock data
          </button>
          <button
            type="button"
            disabled={busy || totalMock === 0}
            onClick={() => setConfirmDelete(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px',
              background: busy || totalMock === 0 ? '#fee2e2' : 'transparent',
              color: '#dc2626', border: '1.5px solid #dc2626', borderRadius: 10,
              fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
              cursor: busy || totalMock === 0 ? 'not-allowed' : 'pointer',
              opacity: busy || totalMock === 0 ? 0.6 : 1,
            }}
          >
            <WIcon name="trash" size={15} color="#dc2626" />
            Delete all mock data
          </button>
        </div>

        <div style={{
          marginTop: 24, padding: '14px 16px',
          background: '#f8fafc', border: '1px dashed #cbd5e1',
          borderRadius: 8, fontSize: 12, color: '#64748b', lineHeight: 1.6,
        }}>
          <strong style={{ color: '#475569' }}>How it works.</strong> Generate creates 4 companies, 8 vendors (with category-matched Unsplash images and matching vendorAdmins entries), 24 offers, 20 employees, 1 <strong>super user</strong>, and 8 vendor staff in Firestore. Employee phones run <code>+9955550000XX</code> (01–20); the super user is <code>+995555099999</code>; vendor staff run <code>+9955551000XX</code> (01–08). Sign in to either Flutter app with any of those phones and OTP <strong>123456</strong>. The super user bypasses both the company and tier filters, so you'll see every active offer. Delete removes everything tagged <code>mockData: true</code> plus any redemptions tied to mock users — real data is untouched.
        </div>
      </div>
    </>
  );
};

export default MockData;
